import localesConverterFilter from '../../blocks/locales/locales';
import SslSupportService from '../../blocks/ssl-support-service/ssl-support-service';
import sniffer from '@jetbrains/ring-ui/components/global/sniffer';

export default class InstallationSettingsController {
  /*@ngInject*/
  constructor($scope, $q, installerResource, fields, i18n, wizardInfo, urlHelper, baseUrlDetector) {
    this.$scope = $scope;
    this.$q = $q;
    this.fields = fields;
    this.i18n = i18n;

    this.urlHelper = urlHelper;
    this.wizardInfo = wizardInfo;
    this.resource = installerResource;
    this.baseUrlDetector = baseUrlDetector;
    this.locales = localesConverterFilter(this.wizardInfo[fields.LOCALES]);
    this.sslSupportService = new SslSupportService();
    this.showTlsSettingsErrors = true;
    this.resolveCertificateHostname();

    if (!this.disableBaseUrlAutoCorrection) {
      this.correctBaseUrl();
    }
  }

  correctBaseUrl() {
    const defaultBaseUrl = this.defaultModel[this.fields.BASE_URL];
    const correctedDefaultBaseUrl = this.baseUrlDetector.getOriginOrDefaultUrl(defaultBaseUrl, this.stepUrl);

    // Update default base URL with the URL user accessed the Wizard by
    this.defaultModel[this.fields.BASE_URL] = correctedDefaultBaseUrl;
    if (this.model[this.fields.BASE_URL] === defaultBaseUrl) {
      // Update base URL with the URL user accessed the Wizard by in case it doesn't differ from the default one.
      this.model[this.fields.BASE_URL] = correctedDefaultBaseUrl;
    }
  }

  shouldReflectListenPortToBaseUrl() {
    const urlFromForm = this.model[this.fields.BASE_URL];
    const defaultUrl = this.defaultModel[this.fields.BASE_URL];

    if (this.getSecureMode() !== this.sslSupportService.DISABLE_MODE) {
      // check only host equality with base URL, it is OK if default base URL was http-based
      return this.urlHelper.getHostOfUrl(urlFromForm) === this.urlHelper.getHostOfUrl(defaultUrl);
    } else {
      // Allow to substitute port in base URL if both host and protocol don't differ from default ones.
      // Changed protocol means that TLS-terminating proxy is used, in that case application listen port differs
      // from proxy port and should not be propagated to base URL (which is in fact a proxy URL)
      return this.urlHelper.getProtocolAndHostFromUrl(urlFromForm) === this.urlHelper.getProtocolAndHostFromUrl(defaultUrl);
    }
  }

  reflectPortToBaseUrl(newPort, oldPort) {
    const currentBaseUrl = this.model[this.fields.BASE_URL];
    const baseUrlPort = this.urlHelper.getPortOfUrl(currentBaseUrl);
    const isSecureMode = (this.getSecureMode() !== this.sslSupportService.DISABLE_MODE);
    const isSamePort = baseUrlPort === oldPort || (oldPort === (isSecureMode ? '443' : '80') && !baseUrlPort);

    if (this.shouldReflectListenPortToBaseUrl() && isSamePort) {
      this.model[this.fields.BASE_URL] = this.urlHelper.changePortInUrl(currentBaseUrl, newPort);
    }
  }

  isShowListenAddress() {
    return this.wizardInfo.isListenAddressEditable || this.forceShowListenAddress;
  }

  checkShowAdvancedSettings() {
    const errorInAdvancedSettings = this.getErrorMessage({field: this.fields.DATA_DIR}) ||
      this.getErrorMessage({field: this.fields.DATA_BACKUP_DIR}) ||
      this.getErrorMessage({field: this.fields.LOGS_DIR}) ||
      this.getErrorMessage({field: this.fields.TEMP_DIR});

    this.showAdvanced = this.showAdvanced || errorInAdvancedSettings;

    return this.showAdvanced;
  }

  showTLSErrors() {
    return this.getSecureMode() !== this.sslSupportService.DISABLE_MODE && this.isOldCertInvalid();
  }

  showSecureRadioButtons() {
    return this.getSecureMode() !== this.sslSupportService.DISABLE_MODE && this.doesValidOldCertExist();
  }

  doesValidOldCertExist() {
    const oldCertInfo = this.model[this.fields.OLD_SERVER_CERT_INFO];
    return oldCertInfo && !this.isOldCertInvalid();
  }

  isOldCertInvalid() {
    const oldCertErrors = this.model[this.fields.OLD_SERVER_CERT_ERRORS];
    return oldCertErrors && oldCertErrors.errors && oldCertErrors.errors.length > 0;
  }

  getSecureErrorMessage() {
    const oldCertErrors = this.model[this.fields.OLD_SERVER_CERT_ERRORS];
    return oldCertErrors && oldCertErrors.errors && oldCertErrors.errors[0] && oldCertErrors.errors[0][this.fields.ERROR_DESCRIPTION];
  }

  isDisabledPath(field) {
    return (this.model.disabledFields || []).indexOf(field) !== -1;
  }

  getDirectoryHintOrDefault(field) {
    const hint = (this.getDirectoryHint) ? this.getDirectoryHint({field, disabled: this.isDisabledPath(field)}) : '';
    if (!hint || hint.length === 0) {
      return this.getDefaultDirectoryHint(field);
    } else {
      return hint;
    }
  }

  getDefaultDirectoryHint(field) {
    switch (field) {
      case this.fields.DATA_DIR:
        return (this.wizardInfo.installationType === 'JAR' && this.isDisabledPath(this.fields.DATA_DIR))
          ? `For a JAR distribution, you cannot update this field in the Configuration Wizard. To change the location of your data directory, restart ${ this.wizardInfo.productName } with the additional command line argument --data-dir=<path> .`
          : `${'Directory where '}${this.wizardInfo.productName}${' stores content data'}`;
      case this.fields.DATA_BACKUP_DIR:
        return `${'Directory where '}${this.wizardInfo.productName}${' stores backups of the database'}`;
      case this.fields.LOGS_DIR:
        return `${'Directory where '}${this.wizardInfo.productName}${' stores log files'}`;
      case this.fields.TEMP_DIR:
        return `${'Directory where '}${this.wizardInfo.productName}${' stores product-specific temporary files'}`;
      default:
        return '';
    }
  }

  getSecureMode() {
    return this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_MODE];
  }

  isRedirectionFromHttpEnabled() {
    return this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.REDIRECTION_FROM_HTTP_FLAG];
  }

  getKeyStoreBuildType() {
    if (this.getSecureMode() !== this.sslSupportService.DISABLE_MODE) {
      return this.model[this.fields.SECURE_CONTENT][this.fields.SECURE_CONTENT_TYPE];
    }
    return null;
  }

  isEncryptedPrivateKey() {
    if (this.getKeyStoreBuildType() === this.sslSupportService.SERVER_CERT_STORAGE_TYPE_PRIVATE_KEY_AND_CERT) {
      const privateKey = this.model[this.fields.SECURE_CONTENT][this.fields.PRIVATE_KEY];
      return privateKey && privateKey.indexOf('ENCRYPTED') !== -1;
    }
    return false;
  }

  isHttpsFeatureFlag() {
    return this.wizardInfo[this.fields.HTTPS_FEATURE_FLAG] === true;
  }

  changeSecureMode(mode) {
    const prefix = 'http://';
    const securePrefix = 'https://';
    let currentBaseUrl = this.model[this.fields.BASE_URL];
    this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_MODE] = mode;

    const defaultNonSecurePort = this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.DEFAULT_NON_SECURE_PORT];
    const defaultSecurePort = this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.DEFAULT_SECURE_PORT];
    const defaultRedirectionFromHttpFlag = this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.DEFAULT_REDIRECTION_FROM_HTTP_FLAG];

    // Set non secure listen port to default if it is not defined yet
    if (!this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.NON_SECURE_LISTEN_PORT]) {
      this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.NON_SECURE_LISTEN_PORT] = defaultNonSecurePort;
    }

    if (mode === this.sslSupportService.TLS_MODE) {
      // Set secure listen port to default if it is not defined yet
      if (!this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_LISTEN_PORT]) {
        this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_LISTEN_PORT] = defaultSecurePort;
      }
      // Set secure listen port to default if it is not defined yet
      if (!this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.REDIRECTION_FROM_HTTP_FLAG]) {
        this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.REDIRECTION_FROM_HTTP_FLAG] = defaultRedirectionFromHttpFlag;
      }
      // fix Base URL
      if (currentBaseUrl.indexOf(prefix) !== -1) {
        currentBaseUrl = securePrefix + currentBaseUrl.substr(prefix.length, currentBaseUrl.length);
      }
      this.model[this.fields.BASE_URL] = this.urlHelper.changePortInUrl(currentBaseUrl,
        this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_LISTEN_PORT]);
    } else {
      // fix Base URL
      if (currentBaseUrl.indexOf(securePrefix) !== -1) {
        currentBaseUrl = prefix + currentBaseUrl.substr(securePrefix.length, currentBaseUrl.length);
      }
      this.model[this.fields.BASE_URL] = this.urlHelper.changePortInUrl(currentBaseUrl,
        this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.NON_SECURE_LISTEN_PORT]);
    }
  }

  changeKeyStoreBuildType(buildType) {
    this.model[this.fields.SECURE_CONTENT][this.fields.SECURE_CONTENT_TYPE] = buildType;
  }

  secureListenPort() {
    return this.model[this.fields.SERVER_CONNECTOR_MODE_SETTING][this.fields.SECURE_LISTEN_PORT];
  }

  cleanKeyStoreErrors() {
    this.clearErrorMessage({field: this.fields.KEYSTORE_FILE_NAME});
    this.clearErrorMessage({field: this.fields.KEYSTORE_PASSWORD});
    this.clearErrorMessage({field: this.fields.KEYSTORE_ALIAS});
    this.clearErrorMessage({field: this.fields.ALIAS_PASSWORD});
    this.clearErrorMessage({field: this.fields.BASE_URL});
    this.clearErrorMessage({field: this.fields.SECURE_LISTEN_PORT});
  }

  cleanPrivateKeyAndCertErrors() {
    this.clearErrorMessage({field: this.fields.PRIVATE_KEY});
    this.clearErrorMessage({field: this.fields.PRIVATE_KEY_PASSPHRASE});
    this.clearErrorMessage({field: this.fields.CERTIFICATE});
    this.clearErrorMessage({field: this.fields.CERTIFICATE_CHAIN});
    this.clearErrorMessage({field: this.fields.BASE_URL});
    this.clearErrorMessage({field: this.fields.SECURE_LISTEN_PORT});
  }

  resolveCertificateHostname() {
    this.resource.cert.save(this.model[this.fields.SECURE_CONTENT]).$promise.then(res => {
      if (res.certificateUrl !== null && res.certificateUrl !== '' && typeof res.certificateUrl !== 'undefined') {
        this.certificateUrl = res.certificateUrl;
      } else {
        this.certificateUrl = null;
      }
    });
  }

  onKeyStoreFieldChanged() {
    this.cleanKeyStoreErrors();
    this.resolveCertificateHostname();
  }

  chooseKeystoreFile(file) {
    this.model[this.fields.SECURE_CONTENT][this.fields.KEYSTORE_FILE] = file.content;
    this.model[this.fields.SECURE_CONTENT][this.fields.KEYSTORE_FILE_NAME] = this.getFileName(file.fullName);
    this.onKeyStoreFieldChanged();
  }

  onPrivateKeyAndCertFieldChanged(modelFile) {
    this.cleanPrivateKeyAndCertErrors();
    if (modelFile === this.fields.CERTIFICATE) {
      this.resolveCertificateHostname();
    }
  }

  chooseKeyCertFile(modelFile, file) {
    this.model[this.fields.SECURE_CONTENT][modelFile] = file.content;
    this.onPrivateKeyAndCertFieldChanged(modelFile);
  }

  setUrlFromCert() {
    this.model[this.fields.BASE_URL] = this.urlHelper.changeHostInUrl(this.model[this.fields.BASE_URL], this.certificateUrl);
  }

  isCertHostInBaseUrl() {
    return typeof this.certificateUrl !== 'undefined' && this.certificateUrl !== null && this.urlHelper.changeHostInUrl(this.model[this.fields.BASE_URL], this.certificateUrl) !== this.model[this.fields.BASE_URL];
  }

  checkFileChoose(modelFileName) {
    return typeof this.model[this.fields.SECURE_CONTENT][modelFileName] !== 'undefined' && this.model[this.fields.SECURE_CONTENT][modelFileName] !== null;
  }

  getFileName(fullPath) {
    if (fullPath) {
      const startIndex = (fullPath.indexOf('\\') >= 0 ? fullPath.lastIndexOf('\\') : fullPath.lastIndexOf('/'));
      let filename = fullPath.substring(startIndex);
      if (filename.indexOf('\\') === 0 || filename.indexOf('/') === 0) {
        filename = filename.substring(1);
      }
      return filename;
    }
    return null;
  }

  isWindowsBrowser() {
    if (sniffer.browser.name === 'edge' || sniffer.browser.name === 'ie') {
      return true;
    }

    return false;
  }

  getLocaleDescription(locale) {
    return locale.isCommunity ? 'community' : null;
  }

  isCommunityLocaleKey(localeKey) {
    return (this.locales.filter(l => l.key === localeKey)[0] || {}).isCommunity;
  }

  getCommunityTranslationsLink() {
    return this.wizardInfo[this.fields.LOCALES_COMMUNITY_LINK];
  }
}



// WEBPACK FOOTER //
// ./src/blocks/installation-settings/installation-settings__controller.js